import React, { FC, useEffect, useCallback, useState } from 'react';
import { Grid, makeStyles, Typography, Button, Tabs, Tab, IconButton, Tooltip } from '@material-ui/core';
import { AddBox, Today } from '@material-ui/icons';
import axios, { CancelTokenSource } from 'axios';
import { GREEN, WHITE } from 'constants/colors';
import { ACCOUNT_CATEGORY_BASE_URL, JOURNAL_TRANSACTIONS_BASE_URL } from 'constants/url';
import useDebounce from 'hooks/useDebounce';
import Pagination from '@material-ui/lab/Pagination';
import useRouter from 'hooks/useRouter';
import { TypeJournalTransactions } from 'pages/TransactionJurnalPage';
import BankHistoryPage from 'pages/BankHistoryPage';
import TransactionTable from './components/TransactionTable';
import { dummyMetaData } from 'utils/dummy';
import { format, startOfMonth } from 'date-fns';
import RefreshIcon from '@material-ui/icons/Refresh';
import DateRangeFilter from 'components/DateRangeFilter';
import GiroPaymentSalesInvoicePage from 'pages/GiroPaymentSalesInvoicePage';
import GiroPurchasePaymentPage from 'pages/GiroPurchasePaymentPage';
import PaymentGiroModal from './components/PaymentGiroModal';
import GiroWithdrawalDebitPage from 'pages/GiroWithdrawalDebitPage';

const useStyles = makeStyles({
  container: {
    rowGap: '2em',
    marginTop: '2em'
  },
  active: {
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '16px'
  },
  breadCrumb: {
    marginTop: '3px'
  },
  refresh: {
    backgroundColor: GREEN,
    color: WHITE,
    '&:hover': {
      backgroundColor: GREEN
    }
  },
  tabPanel: {
    flexGrow: 1,
    marginBottom: '20px',
    display: 'flex',
    justifyContent: 'flex-start'
  }
});

const dummyAccountCategory: AccountCategoryModel = {
  id: 0,
  name: '',
  category: '',
  number: 0,
  subAccount: 0,
  isEditable: true,
  isActive: true,
  subItemAccount: []
};

interface RoleModel {
  name: string;
}

const GiroWithdrawalPage: FC = () => {
  const classes = useStyles();
  const { history } = useRouter();
  const [open, setOpen] = useState<boolean>(false);
  const [query, setQuery] = useState<string>('');
  const [journalTransactionsId, setJournalTransactionsId] = useState<transactionJurnal | null>(null);
  const [queryString, setQueryString] = useState<string>();
  const [endDate, setEndDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [startDate, setStartDate] = useState<string>(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [accountCategories, setAccountCategories] = useState<AccountCategoryModel[]>([dummyAccountCategory]);
  const [akun, setAkun] = useState<string>('');
  const [meta, setMeta] = useState<MetaData>(dummyMetaData);
  const [wareHouse, setWareHouse] = useState<WareHouseModel | null>(null);
  const [subAccountCategories, setSubAccountCategories] = useState<AccountCategoryModel[]>([dummyAccountCategory]);
  const [accountCategorie, setAccountCategorie] = useState<AccountCategoryModel | null>(null);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState<string>('id');
  const [name, setName] = useState<string>('');
  const [address, setAddress] = useState<string>('');
  const [openCalendarFilter, setOpenCalendarFilter] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<number>();
  const [confirmationDelete, setConfirmationDelete] = useState<boolean>(false);
  const [snackbarVariant, setSnackbarVariant] = useState<'success' | 'error'>('success');
  const [message, setMessage] = useState<string>('');
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [tab, setTab] = React.useState<number>(0);
  const [isPackage, setPackage] = useState<boolean>(false);
  const [transaksi, setTransaksi] = useState<string>('');
  const [openCollapse, setOpenCollapse] = useState<boolean>(false);
  const [indexCollapse, setIndexCollapse] = useState<number>(-1);
  const source = axios.CancelToken.source();
  const cancelToken = source.token;
  const [journalTransactions, setJournalTransactions] = useState<TypeJournalTransactions>({
    isLoading: false,
    data: []
  });
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [authorizaion, setAuthorizaion] = useState<boolean>(false);
  const [giroMonthDue, setGiroMonthDue] = useState<{ total: Number; month: string }[]>([]);
  const [openGiroModal, setOpenGiroModal] = useState<boolean>(false);

  const fetchData = useCallback(async () => {
    setJournalTransactions(prev => ({ ...prev, isLoading: true }));
    const getQueryParams = () => {
      const params = new URLSearchParams();
      if (authorizaion) {
        params.append('auhtorization', 'true');
      }
      if (queryString) {
        params.append('keyword', queryString);
      }
      if (transaksi) {
        params.append('transaksi', transaksi);
      }
      if (akun) {
        params.append('akun', akun);
      }
      params.append('page', currentPage.toString());

      if (!authorizaion) {
        params.append('startDate', format(new Date(startDate), 'yyyy-MM-dd'));
        params.append('endDate', format(new Date(endDate), 'yyyy-MM-dd'));
      }
      params.append('paymentGiro', 'true');
      params.append('credit', 'true');
      params.append('reject', 'false');
      if (orderBy || order) {
        params.append('orderBy', orderBy);
        params.append('ordered', order);
      }
      return params;
    };

    try {
      const { data } = await axios.get(`${JOURNAL_TRANSACTIONS_BASE_URL}?${getQueryParams()}`, {
        cancelToken
      });
      setJournalTransactions(prev => ({ ...prev, isLoading: false, data: data.data }));
      setMeta(data.meta);
    } catch (error) {
      console.log(error);
    }
  }, [queryString, order, orderBy, startDate, endDate, currentPage, authorizaion]);

  const performActionAndRevertPage = (action: React.Dispatch<React.SetStateAction<any>>, actionParam: any) => {
    setCurrentPage(0);
    action(actionParam);
  };

  const handleSearch = useCallback((searchQuery: string) => {
    performActionAndRevertPage(setQueryString, searchQuery);
  }, []);

  const debouncedSearchTerm = useDebounce(query, 500);

  useEffect(() => {
    if (debouncedSearchTerm.length >= 3) {
      handleSearch(debouncedSearchTerm);
    } else if (debouncedSearchTerm.length === 0) {
      handleSearch(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, handleSearch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTabs = (event: React.ChangeEvent<{}>, newValue: number): void => {
    setTab(newValue);

    setPackage(newValue === 1);
  };

  const handleSnackBar = (open: boolean, variant: 'success' | 'error', message: string) => {
    setSnackbarVariant(variant);
    setOpenSnackbar(open);
    setMessage(message);
  };

  const handleOpenGiroModal = () => {
    setOpenGiroModal(true);
  };
  const onClickHandler = (path: string): React.MouseEventHandler => event => {
    event.preventDefault();
    history.push(path);
  };
  const getData = async (id: number) => {
    const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();
    try {
      await axios.get(`${ACCOUNT_CATEGORY_BASE_URL}/akun/${id}`, { cancelToken: cancelTokenSource.token });
      setSubAccountCategories(accountCategories.filter(value => value.id !== selectedId));
    } catch (err) {
      console.log(err);
    }
    setConfirmationDelete(false);
  };

  const handleCalendarFilterClick = () => setOpenCalendarFilter(true);
  const handleAction = (isOpen: boolean, id: number, action: 'delete' | 'update' | 'detail' | 'auhtorization' = 'delete', type: string) => {};
  const onRefresh = () => {
    setOrderBy('id');
    setOrder('desc');
    setQueryString('');
    setCurrentPage(1);
    setStartDate(format(new Date(startDate), 'yyyy-MM-dd'));
    setEndDate(format(new Date(endDate), 'yyyy-MM-dd'));
    fetchData();
  };

  const handleCloseGiroModal = () => {
    setOpenGiroModal(false);
  };

  const handleUpdatePaymentIndividual = (id: number, data: InvoicePaymentModel) => {
    // const currentInvoicePayments = [...invoicePayments];
    // const findIndex = currentInvoicePayments.findIndex(invoice => invoice.id === id);
    // currentInvoicePayments[findIndex] = data;
    // setInvoicePayments(currentInvoicePayments);
  };

  return (
    <Grid container justify='flex-start' item xl={12} lg={12} md={12} sm={12} xs={12}>
      <Grid>
        <Grid container className={classes.tabPanel}>
          <Tabs value={tab} indicatorColor='primary' textColor='primary' onChange={handleTabs} aria-label='tab-produk'>
            {/* <Tab label='Penjualan' />
            <Tab label='Pembelian' /> */}
            <Tab label='Transaksi Jurnal Umum Pemasukan' />
            <Tab label='Transaksi Jurnal Umum Pengeluaran' />
          </Tabs>
        </Grid>

        <PaymentGiroModal
          giroMonthDue={giroMonthDue}
          setOpenSnackbar={setOpenSnackbar}
          openModal={openGiroModal}
          handleCloseModal={handleCloseGiroModal}
          handleSnackBar={handleSnackBar}
          handleUpdatePaymentIndividual={handleUpdatePaymentIndividual}
        />
      </Grid>
      {tab === 1 && (
        <>
          <Grid container direction='row' spacing={2}>
            <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
              <Grid container direction='row' justify='space-between'>
                <Grid item xl={7} lg={7} md={12} sm={12} xs={12} container alignItems='center'>
                  <Tooltip title='Memuat Ulang'>
                    <Button size='small' onClick={onRefresh} color='inherit' className={classes.refresh}>
                      <RefreshIcon fontSize='small' />
                    </Button>
                  </Tooltip>
                  <Typography style={{ fontSize: '1rem', fontWeight: 400, marginLeft: '15px' }}>
                    {`Menampilkan ${meta.total || 0} Transaksi (${meta.from || 0} - ${meta.to || 0} dari ${meta.total || 0})`}
                    <Typography color='primary'>
                      Data tanggal {format(new Date(startDate), 'dd-MM-yyyy')} s/d {format(new Date(endDate), 'dd-MM-yyyy')}
                    </Typography>
                  </Typography>
                </Grid>

                <Grid container item lg={4} sm={4} md={4} justify='flex-end' alignItems='center'>
                  <Grid item>
                    <Tooltip title='Filter Tanggal' placement='top'>
                      <IconButton color='primary' aria-label='filter date' component='span' onClick={handleCalendarFilterClick}>
                        <Today />
                      </IconButton>
                    </Tooltip>
                    <Button disabled={isLoadingData} onClick={handleOpenGiroModal}>
                      <AddBox fontSize='small' /> &nbsp; Pencairan Giro
                    </Button>
                    {/* <Button aria-controls='simple-menu' aria-haspopup='true' onClick={handleClick}>
                      Tambah Transaksi
                    </Button> */}
                    {/* <Menu id='simple-menu' anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleClose}> */}
                    {/* <MenuItem onClick={handleOpenCreateTransferInternal}>Pemindahan Internal</MenuItem>
                      <MenuItem onClick={handleOpenCreate}>Buat Pemasukan Lain</MenuItem>
                      <MenuItem onClick={handleOpenCreateOtherExpenses}>Buat Pengeluaran Lain</MenuItem> */}
                    {/* </Menu> */}
                    {/* {isSuperAdmin && !authorizaion && (
                      <Badge
                        color='error'
                        badgeContent={totalAuthorization}
                        anchorOrigin={{
                          vertical: 'top',
                          horizontal: 'right'
                        }}
                      >
                        <Description
                          fontSize='large'
                          color='primary'
                          style={{
                            marginLeft: 12,
                            cursor: 'pointer',
                            pointerEvents: 'auto'
                          }}
                          //className={totalAuthorization > 0 ? classes.animationNotif : ''}
                          onClick={() => handleOpenAuthorization()}
                          onMouseEnter={() => setIsShown(true)}
                          onMouseLeave={() => setIsShown(false)}
                        />
                      </Badge>
                    )}
                    {isSuperAdmin && authorizaion && (
                      <IconButton color='primary' aria-label='back' component='span' className={classes.buttonBack}>
                        <ExitToApp fontSize='large' onClick={handleCloseAuthorization} />
                      </IconButton>
                    )} */}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid xs={12} container className={classes.container}>
            <Grid xs={12}>
              <TransactionTable
                setOrder={setOrder}
                setOrderBy={setOrderBy}
                setTransaksi={setTransaksi}
                setAkun={setAkun}
                akun={akun}
                transaksi={transaksi}
                journalTransactions={journalTransactions}
                handleAction={handleAction}
              />
            </Grid>
            {journalTransactions.data.length > 0 && (
              <Grid xs={12} container>
                <Pagination
                  count={meta.last_page}
                  onChange={(event, page) => setCurrentPage(page)}
                  page={meta.current_page}
                  boundaryCount={2}
                  variant='outlined'
                  shape='rounded'
                />
              </Grid>
            )}
            <DateRangeFilter
              openCalendarFilter={openCalendarFilter}
              startDate={startDate}
              endDate={endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              handleClose={() => setOpenCalendarFilter(false)}
            />
          </Grid>
        </>
      )}
      {/* {tab === 0 && <GiroPaymentSalesInvoicePage />}
      {tab === 1 && <GiroPurchasePaymentPage />} */}
      {tab === 0 && <GiroWithdrawalDebitPage />}
    </Grid>
  );
};

export default GiroWithdrawalPage;
